import * as FC from "@fluentui/react-components";
import * as FDP from "@fluentui/react-datepicker-compat";
import * as IC from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import { toDateString } from "../../date_utils";
import { useToast } from "../../hooks/useToast";
import { apiClient } from "../../services/api";
import {
    createServiceRecord,
    equipmentErrorCodes,
    occupyingSessionsFromError,
    equipmentErrorTrace,
    isEquipmentErrorCode,
    parseDateOnly,
    serviceTypeOptions,
    serviceUsageUnitOptions,
    toDateOnly,
    updateServiceRecord,
} from "../../services/equipment_service";

const useStyles = FC.makeStyles({
    form: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("12px"),
    },
    row: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        ...FC.shorthands.gap("10px"),
    },
    actions: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
    },
    hint: {
        color: FC.tokens.colorNeutralForeground3,
    },
});

const emptyForm = {
    serviceType: "repair",
    reportedDate: null,
    startedDate: null,
    faultDescription: "",
    serviceContent: "",
    result: "",
    serviceProvider: "",
    technicianName: "",
    handledByUserId: "",
    costAmount: "",
    currency: "CNY",
    usageValueAtService: "",
    usageUnit: "",
    nextDueDate: null,
    startImmediately: false,
};

const toFormValues = (record) => {
    if (!record) {
        return { ...emptyForm };
    }
    return {
        serviceType: record.serviceType || "repair",
        reportedDate: parseDateOnly(record.reportedDate),
        startedDate: parseDateOnly(record.startedDate),
        faultDescription: record.faultDescription || "",
        serviceContent: record.serviceContent || "",
        result: record.result || "",
        serviceProvider: record.serviceProvider || "",
        technicianName: record.technicianName || "",
        handledByUserId: record.handledByUserId ? String(record.handledByUserId) : "",
        costAmount: record.costAmount === null || record.costAmount === undefined ? "" : String(record.costAmount),
        currency: record.currency || "CNY",
        usageValueAtService:
            record.usageValueAtService === null || record.usageValueAtService === undefined
                ? ""
                : String(record.usageValueAtService),
        usageUnit: record.usageUnit || "",
        nextDueDate: parseDateOnly(record.nextDueDate),
        startImmediately: false,
    };
};

const trimmedOrNull = (value) => {
    const text = (value ?? "").toString().trim();
    return text === "" ? null : text;
};

const numberOrNull = (value) => {
    const text = (value ?? "").toString().trim();
    if (text === "") {
        return null;
    }
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : null;
};

const DateField = ({ label, value, onChange, hint, error, disabled }) => (
    <FC.Field
        label={label}
        hint={hint}
        validationState={error ? "error" : "none"}
        validationMessage={error || undefined}
    >
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <FDP.DatePicker
                style={{ flex: 1 }}
                disabled={disabled}
                value={value || null}
                placeholder="选择日期"
                formatDate={toDateString}
                onSelectDate={(date) => onChange(date || null)}
            />
            <FC.Button
                appearance="subtle"
                aria-label={`清除${label}`}
                disabled={disabled || !value}
                icon={<IC.DismissRegular />}
                onClick={() => onChange(null)}
            />
        </div>
    </FC.Field>
);

/**
 * Create / edit form for a maintenance or repair work order.
 *
 * Deliberately absent: status, record number, the resulting equipment status and every
 * completion or cancellation timestamp. Those are owned by the lifecycle endpoints
 * (start / complete / cancel / void) and a stale edit form must not be able to write them.
 * `completedDate` is likewise not here — completion supplies it, defaulting to the server's
 * Asia/Shanghai business date, which a browser in another offset cannot compute.
 */
export const ServiceRecordForm = ({ equipment, record, onSaved, onCancel }) => {
    const styles = useStyles();
    const showToast = useToast();
    const submittingRef = useRef(false);
    // Held in a ref, not state: the re-submit reads it in the same tick the user confirms, and a
    // state update would not have landed yet.
    const releaseFromSessionIdRef = useRef(null);

    const isEdit = Boolean(record?.id);
    const [form, setForm] = useState(() => toFormValues(record));
    const [fieldErrors, setFieldErrors] = useState({});
    const [formError, setFormError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // { sessions, close } once the server reports the unit is out on a job.
    const [occupancyChoice, setOccupancyChoice] = useState(null);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        setForm(toFormValues(record));
        setFieldErrors({});
        setFormError(null);
    }, [record]);

    useEffect(() => {
        apiClient
            .getUsers()
            .then((items) => setUsers(items || []))
            .catch((error) => console.error("Failed to load users:", error));
    }, []);

    const patch = (values) => setForm((previous) => ({ ...previous, ...values }));

    const buildPayload = () => {
        const shared = {
            serviceType: form.serviceType,
            reportedDate: toDateOnly(form.reportedDate),
            startedDate: toDateOnly(form.startedDate),
            faultDescription: trimmedOrNull(form.faultDescription),
            serviceContent: trimmedOrNull(form.serviceContent),
            result: trimmedOrNull(form.result),
            serviceProvider: trimmedOrNull(form.serviceProvider),
            technicianName: trimmedOrNull(form.technicianName),
            handledByUserId: form.handledByUserId ? Number(form.handledByUserId) : null,
            costAmount: numberOrNull(form.costAmount),
            currency: trimmedOrNull(form.currency),
            usageValueAtService: numberOrNull(form.usageValueAtService),
            usageUnit: trimmedOrNull(form.usageUnit),
            nextDueDate: toDateOnly(form.nextDueDate),
        };

        return isEdit
            ? { ...shared, recordVersion: record.version }
            : {
                  ...shared,
                  startImmediately: Boolean(form.startImmediately),
                  // Only ever set after the user has picked which job to take the unit off; see
                  // the occupancy prompt below.
                  releaseFromSessionId: releaseFromSessionIdRef.current,
              };
    };

    /**
     * Field-level errors come back keyed by DTO property name (PascalCase) while the form state is
     * camelCase; one field can carry several messages, joined onto one line.
     */
    const applyValidationDetails = (details) => {
        if (!details) {
            return false;
        }
        const mapped = {};
        Object.entries(details).forEach(([key, messages]) => {
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            mapped[field] = Array.isArray(messages) ? messages.join("；") : String(messages);
        });
        if (Object.keys(mapped).length === 0) {
            return false;
        }
        setFieldErrors(mapped);
        return true;
    };

    const handleError = (error) => {
        const message = error?.message || "保存失败";
        const traceId = error?.traceId || "";

        if (
            isEquipmentErrorCode(error, equipmentErrorCodes.RequestValidationFailed) &&
            applyValidationDetails(error?.details)
        ) {
            setFormError({ message, traceId });
            return;
        }

        // These name a specific field, so they are shown next to it rather than only in the banner.
        const fieldFor = {
            [equipmentErrorCodes.InvalidCurrency]: "currency",
            [equipmentErrorCodes.InvalidCost]: "costAmount",
            [equipmentErrorCodes.InvalidUsageUnit]: "usageUnit",
            [equipmentErrorCodes.InvalidDateRange]: "startedDate",
        }[error?.errorCode];

        if (fieldFor) {
            setFieldErrors((previous) => ({ ...previous, [fieldFor]: message }));
        }

        setFormError({ message, traceId });
    };

    const submit = async (close) => {
        if (submittingRef.current) {
            return;
        }
        submittingRef.current = true;
        setSubmitting(true);
        setFieldErrors({});
        setFormError(null);

        try {
            const payload = buildPayload();
            const saved = isEdit
                ? await updateServiceRecord(equipment.id, record.id, payload)
                : await createServiceRecord(equipment.id, payload);
            showToast(isEdit ? "维保记录已保存" : "维保记录已创建", "success");
            onSaved?.({ close, record: saved });
        } catch (error) {
            // "报修并立即送修" against a unit that is out on a job (design 4.2). The server names
            // the sessions holding it; the user picks one, and only then does the request carry
            // releaseFromSessionId. Choosing on their behalf would end a field occupancy nobody
            // agreed to end.
            const sessions = occupyingSessionsFromError(error);
            if (sessions.length > 0 && !releaseFromSessionIdRef.current) {
                setOccupancyChoice({ sessions, close });
                return;
            }

            console.error("Failed to save service record:", error);
            handleError(error);
        } finally {
            releaseFromSessionIdRef.current = null;
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const confirmOccupancyRelease = async (sessionId) => {
        const pending = occupancyChoice;
        setOccupancyChoice(null);
        releaseFromSessionIdRef.current = sessionId;
        await submit(pending?.close ?? true);
    };

    return (
        <div className={styles.form}>
            <FC.Body1 className={styles.hint}>
                {`${equipment?.assetCode || ""} ${equipment?.name || ""}`}
                {isEdit && record?.recordNo ? ` · ${record.recordNo}` : ""}
            </FC.Body1>

            {formError && (
                <FC.MessageBar intent="error">
                    <FC.MessageBarBody>
                        {formError.message}
                        {formError.traceId ? `（${equipmentErrorTrace(formError)}）` : ""}
                    </FC.MessageBarBody>
                </FC.MessageBar>
            )}

            <div className={styles.row}>
                <FC.Field
                    label="维保类型"
                    required
                    validationState={fieldErrors.serviceType ? "error" : "none"}
                    validationMessage={fieldErrors.serviceType}
                    hint="只有保养完成才会推进下次保养日期"
                >
                    <FC.Dropdown
                        selectedOptions={[form.serviceType]}
                        value={serviceTypeOptions.find((o) => o.value === form.serviceType)?.label || ""}
                        onOptionSelect={(_, data) => patch({ serviceType: data.optionValue })}
                    >
                        {serviceTypeOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </FC.Field>
                <FC.Field
                    label="经办人"
                    validationState={fieldErrors.handledByUserId ? "error" : "none"}
                    validationMessage={fieldErrors.handledByUserId}
                >
                    <FC.Dropdown
                        selectedOptions={form.handledByUserId ? [form.handledByUserId] : [""]}
                        value={users.find((u) => String(u.id) === form.handledByUserId)?.name || "未指定"}
                        onOptionSelect={(_, data) => patch({ handledByUserId: data.optionValue || "" })}
                    >
                        <FC.Option value="">未指定</FC.Option>
                        {users.map((user) => (
                            <FC.Option key={user.id} value={String(user.id)} text={user.name}>
                                {user.name}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </FC.Field>
            </div>

            <div className={styles.row}>
                <DateField
                    label="报告日期"
                    value={form.reportedDate}
                    onChange={(date) => patch({ reportedDate: date })}
                    error={fieldErrors.reportedDate}
                />
                <DateField
                    label="开始日期"
                    value={form.startedDate}
                    onChange={(date) => patch({ startedDate: date })}
                    hint="不得早于报告日期"
                    error={fieldErrors.startedDate}
                />
            </div>

            <FC.Field
                label="故障或保养原因"
                validationState={fieldErrors.faultDescription ? "error" : "none"}
                validationMessage={fieldErrors.faultDescription}
            >
                <FC.Textarea
                    rows={3}
                    value={form.faultDescription}
                    onChange={(_, data) => patch({ faultDescription: data.value })}
                />
            </FC.Field>

            <div className={styles.row}>
                <FC.Field label="服务商" validationMessage={fieldErrors.serviceProvider}>
                    <FC.Input
                        value={form.serviceProvider}
                        onChange={(_, data) => patch({ serviceProvider: data.value })}
                    />
                </FC.Field>
                <FC.Field label="维修/保养人员" validationMessage={fieldErrors.technicianName}>
                    <FC.Input
                        value={form.technicianName}
                        onChange={(_, data) => patch({ technicianName: data.value })}
                    />
                </FC.Field>
            </div>

            <div className={styles.row}>
                <FC.Field
                    label="费用"
                    validationState={fieldErrors.costAmount ? "error" : "none"}
                    validationMessage={fieldErrors.costAmount}
                >
                    <FC.Input
                        type="number"
                        min={0}
                        value={form.costAmount}
                        onChange={(_, data) => patch({ costAmount: data.value })}
                    />
                </FC.Field>
                <FC.Field
                    label="币种"
                    hint="ISO 4217 三字码，留空按 CNY"
                    validationState={fieldErrors.currency ? "error" : "none"}
                    validationMessage={fieldErrors.currency}
                >
                    {/* Free text on purpose: the server accepts the whole standard, and mirroring a
                        178-entry table here would be one more copy to drift. */}
                    <FC.Input
                        maxLength={3}
                        value={form.currency}
                        onChange={(_, data) => patch({ currency: data.value.toUpperCase() })}
                    />
                </FC.Field>
            </div>

            <div className={styles.row}>
                <FC.Field label="使用量" validationMessage={fieldErrors.usageValueAtService}>
                    <FC.Input
                        type="number"
                        value={form.usageValueAtService}
                        onChange={(_, data) => patch({ usageValueAtService: data.value })}
                    />
                </FC.Field>
                <FC.Field
                    label="使用量单位"
                    validationState={fieldErrors.usageUnit ? "error" : "none"}
                    validationMessage={fieldErrors.usageUnit}
                >
                    <FC.Dropdown
                        selectedOptions={form.usageUnit ? [form.usageUnit] : [""]}
                        value={serviceUsageUnitOptions.find((o) => o.value === form.usageUnit)?.label || "未指定"}
                        onOptionSelect={(_, data) => patch({ usageUnit: data.optionValue || "" })}
                    >
                        <FC.Option value="">未指定</FC.Option>
                        {serviceUsageUnitOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </FC.Field>
            </div>

            <DateField
                label="下次到期日"
                value={form.nextDueDate}
                onChange={(date) => patch({ nextDueDate: date })}
                hint="完成时必须晚于完成日期，否则会被拒绝"
                error={fieldErrors.nextDueDate}
            />

            {!isEdit && (
                <FC.Checkbox
                    checked={form.startImmediately}
                    label="立即开始处理（器材转入维修保养中）"
                    onChange={(_, data) => patch({ startImmediately: Boolean(data.checked) })}
                />
            )}

            <FC.Dialog
                open={Boolean(occupancyChoice)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setOccupancyChoice(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>结束巡检占用并送修</FC.DialogTitle>
                        <FC.DialogContent>
                            <div className={styles.form}>
                                <FC.Body1 className={styles.hint}>
                                    该器材正被以下巡检会话占用。确认后将在同一事务中结束占用区间，再开始维保。
                                </FC.Body1>
                                {(occupancyChoice?.sessions || []).map((session) => (
                                    <FC.Button
                                        key={session.sessionId}
                                        appearance="primary"
                                        onClick={() => confirmOccupancyRelease(session.sessionId)}
                                    >
                                        {`结束会话 ${session.sessionSeq} 的占用并开始维保`}
                                    </FC.Button>
                                ))}
                            </div>
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setOccupancyChoice(null)}>
                                取消
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            <div className={styles.actions}>
                <FC.Button appearance="primary" disabled={submitting} onClick={() => submit(true)}>
                    {submitting ? "保存中..." : "保存"}
                </FC.Button>
                {!isEdit && (
                    <FC.Button disabled={submitting} onClick={() => submit(false)}>
                        保存并继续新增
                    </FC.Button>
                )}
                <FC.Button appearance="secondary" disabled={submitting} onClick={onCancel}>
                    取消
                </FC.Button>
            </div>
        </div>
    );
};
